import React, { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useLocation } from "react-router-dom";
import { connect } from "react-redux";

import * as SearchActions from "../../../middleware/Store/Actions/SearchActions";

import Markdown, { ResultDisplayMode } from "../Documentation/Markdown2HTML";
import Card from "../../UI/Card/Card";
import CardDocument from "../../UI/Card/CardDocument";
import CardTabs from "../../UI/Card/CardTabs";
import Headline from '../../Headline/Headline';

import * as SearchUtils from "../../Utils/SearchUtils";
import * as SearchConstant from "../../Constants/SearchConstant";

import LogService from "../../../middleware/CommonSdk/LogService";

const LOG_TAG = "SEARCH";
const ResultsPerTabCount = 10;

const Search = (props) => {
    const [searchValue, setSearchValue] = useState("");
    const [searchFilters, setSearchFilters] = useState("");
    const [searchTabIndex, setSearchTabIndex] = useState(0);
    const [displayResultItems, setDisplayResultItems] = useState(null);
    const [progress, setProgress] = useState(false);
    const intl = useIntl();
    const location = useLocation();

    useEffect(() => {
        LogService.trace(LOG_TAG, "useEffect location");
        if (props.isSearchAvailable && SearchUtils.isSearchPath(location)) {
            setSearchValue(SearchUtils.getSearchValue(location));
            const foundSearchFilter = SearchUtils.getSearchFilterList(location);
            if (!SearchUtils.hasSameContentList(searchFilters, foundSearchFilter)) {
                setSearchFilters(foundSearchFilter);
            }
            const tabIndex = Number(SearchUtils.getSearchTabIndex(location)) || 0;
            setSearchTabIndex(tabIndex);
        }
    }, [location]);

    useEffect(() => {
        LogService.trace(LOG_TAG, "useEffect searchValue searchFilters");
        if (searchValue) {
            startSearch();
        }
    }, [searchValue, searchFilters]);

    const parseSearchResult = (searchValue, searchResultArray) => {
        LogService.trace(LOG_TAG, "parseSearchResult");

        let searchResultDisplayItem = [];

        if (!searchResultArray || searchResultArray.length === 0) {
            LogService.warn(LOG_TAG, "[parseSearchResult] no result found");
            return Promise.resolve(searchResultDisplayItem);
        }

        searchResultArray.map((lunrSearchResultItem, i) => {
            let tabTitle = SearchUtils.getDisplayNameFromFilterItem(lunrSearchResultItem.filter);
            tabTitle = intl.messages[lunrSearchResultItem.filter] ? intl.messages[lunrSearchResultItem.filter] : tabTitle;
            const resultPath = lunrSearchResultItem.resultPath;

            const fileResult = lunrSearchResultItem.result?.map((element, j) => {
                const docLocation = {
                    pathname: SearchConstant.DOC_PATH + resultPath + element.ref,
                    search: "?" + SearchConstant.SearchTagName + "=" + searchValue,
                    hash: ""
                };

                const markdowPage =
                    <CardDocument key={"searchResult_" + i + "_" + j} showFiligram={SearchUtils.isStsDocumentation(docLocation.pathname)} showBadge={SearchUtils.hasBetaInUri(docLocation.search)}>
                        <Markdown
                            srcLocation={docLocation}
                            displayMode={ResultDisplayMode.SearchSummary}
                            key={docLocation.pathname} />
                    </CardDocument>
                return markdowPage;
            });
            let content = fileResult;
            if (fileResult && fileResult.length === 0) {//no content
                content = <h4 className="text-warning">{intl && intl.messages.searchNoResultFound}</h4>
            }

            let newResult = {
                title: tabTitle + " (" + (content.length ?? 0) + ")",
                content: content
            };
            const foundResultIndex = searchResultDisplayItem.findIndex(item => item.title.startsWith(tabTitle));
            if (foundResultIndex >= 0) {//Csharp treatment (concat results in one tab)
                const existingLength = searchResultDisplayItem[foundResultIndex].content.length ?? 0;
                if (existingLength > 0 && content.length > 0) {
                    const newLength = existingLength + content.length;
                    newResult = {
                        title: tabTitle + " (" + newLength + ")",
                        content: [...searchResultDisplayItem[foundResultIndex].content, ...content]
                    };
                } else if (existingLength > 0) {
                    newResult = {
                        title: tabTitle + " (" + existingLength + ")",
                        content: [...searchResultDisplayItem[foundResultIndex].content]
                    };
                }
            }
            searchResultDisplayItem = [...searchResultDisplayItem.filter(item => !item.title.startsWith(tabTitle)), newResult];
        });

        return Promise.resolve(searchResultDisplayItem);
    };

    const startSearch = () => {
        LogService.trace(LOG_TAG, "startSearch");

        if (SearchUtils.canStartSearch(searchValue, searchFilters)) {
            const searchText = SearchUtils.filterSearchValue(searchValue);

            //launch search
            LogService.info(LOG_TAG, "startSearch with:", searchText);
            setProgress(true);
            props.searchText(searchText, searchFilters)
                .then(searchResults => {
                    LogService.info(LOG_TAG, "startSearch searchText success");

                    if (searchResults?.length > 0) {
                        const filteredSearchText = searchText.replace(/[+-]/g, "");//remove lunr AND operator for highlight
                        parseSearchResult(filteredSearchText, searchResults)
                            .then((searchResultDisplayItem) => {
                                LogService.trace(LOG_TAG, "useEffect searchResult parseSearchResult callback");
                                setDisplayResultItems(searchResultDisplayItem);
                                setProgress(false);
                            });
                    } else {
                        setDisplayResultItems(null);
                        setProgress(false);
                    }
                });
        } else {
            LogService.warn(LOG_TAG, "startSearch search value too short or no filter selected. skip");
            setDisplayResultItems(null);
        }
    };

    LogService.info(LOG_TAG, "render");

    const title = intl.messages.searchTitle;
    let subtitle = intl.messages.searchMessage;

    let displayResult = <Card>{intl && intl.messages.searchNoResultFound}</Card>;
    if (displayResultItems?.length > 0) {
        displayResult = <CardTabs
            tabItems={displayResultItems}
            enableMoreButton={true}
            moreButtonThreshold={ResultsPerTabCount}
            lastTabIndex={searchTabIndex}
        />
    }

    return (
        <div>
            <Headline
                id="headline"
                title={title}
                subtitle={subtitle}
            />
            <div id="searchResultAera" className="row">
                <div className="col">
                    {displayResult}
                </div>
            </div>
        </div>
    );
}

Search.propTypes = {
};

const mapStateToProps = state => {
    return {
        isSearchAvailable: SearchUtils.isSearchAvailable(state.search.indexFiles)
    };
};

const mapDispatchToProps = dispatch => {
    return {
        searchText: (value, filter) => dispatch(SearchActions.searchText(value, filter))
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(Search);